<template>
  <div v-if="n" class="ly-tline">
    <div class="ly-tl-head">
      <span class="tlabel">лента времени</span>
      <span class="ly-tl-info">{{ n }} {{ plural(n, ['карточка', 'карточки', 'карточек']) }}<template v-if="chrono">
        · {{ dayLabel(minD) }} — {{ dayLabel(maxD) }} · {{ maxD - minD }} {{ plural(maxD - minD, ['день', 'дня', 'дней']) }}</template><template v-if="range">
        · <b class="ly-tl-acc">в окне {{ inWin }}</b></template></span>
      <button v-if="range" type="button" class="tgl ly-ghost" data-help="лента/весь период"
              @click="$emit('range', null)">весь период</button>
      <span class="ly-tl-lg"><b style="background:var(--accent)"></b>блок оттока</span>
      <span class="ly-tl-lg"><b style="background:var(--ph0)"></b>цвет сверху — фраза</span>
      <span class="ly-tl-sw">
        <button type="button" class="tgl ly-tl-mini" :aria-pressed="String(mode === 'scroll')" data-help="лента/прокрутка"
                @click="setMode('scroll')">прокрутка</button>
        <button type="button" class="tgl ly-tl-mini" :aria-pressed="String(mode === 'window')" data-help="лента/окно"
                @click="setMode('window')">окно</button>
      </span>
      <span v-if="storyBlock !== null" class="ly-tl-sw">
        <span class="tlabel">шарики</span>
        <button type="button" class="tgl ly-tl-mini" :aria-pressed="String(dotT)" data-help="лента/заголовок"
                @click="dotT = !dotT">заголовок</button>
        <button type="button" class="tgl ly-tl-mini" :aria-pressed="String(dotS)" data-help="лента/подзаголовок"
                @click="dotS = !dotS">подзаголовок</button>
      </span>
      <span class="tlabel ly-tl-how">{{ mode === 'scroll'
        ? 'тяни или кликай — сцена едет к этой карточке, остальные не прячутся'
        : 'протяни — окно · клик — эта карточка' }}</span>
    </div>

    <div ref="track" class="ly-tl-track" :class="{ 'is-scroll': mode === 'scroll' }"
         @mousedown="onDown" @mousemove="onHover" @mouseleave="hover = null">
      <div v-if="selRect" class="ly-tl-sel" :style="selRect"></div>
      <!-- «бегунок»: отрезок карточек, реально видимых сейчас в сцене -->
      <div v-if="view" class="ly-tl-view"
           :style="{ left: (view.a / n * 100) + '%', width: Math.max(1.5, (view.b + 1 - view.a) / n * 100) + '%' }"></div>
      <i v-for="(b, i) in bars" :key="b.id" class="ly-tl-bar"
         :class="{ 'is-off': !b.on, 'is-conv': b.conv, 'is-here': i === here }" :style="b.style">
        <u :style="{ background: b.color }"></u>
        <s v-for="k in nB" :key="k" :class="{ 'is-on': k === b.hb }"></s>
      </i>
      <!-- курсор стоит на столбике передней карточки — той же, что у панели чтения -->
      <div v-if="here != null && here < n" class="ly-tl-cursor" :style="{ left: ((here + 0.5) / n * 100) + '%' }"></div>
      <div v-if="tip" class="ly-tl-tip" :style="{ left: tip.x + '%' }">
        <b>Сессия {{ numOf ? numOf(tip.u) : hover + 1 }}</b>
        <span>{{ dayLabel(tip.u.day) }} · {{ tip.u.phrase }} · {{ tip.u.churn >= 0 ? 'отток в блоке ' + (tip.u.churn + 1) : noChurnTxt(tip.u) }}</span>
        <span v-if="tip.u.convMask" class="ly-tl-tipconv">✓ конверсия</span>
      </div>
    </div>

    <template v-if="storyBlock !== null">
      <div v-for="row in dotRows" :key="row.key" class="ly-tl-dots" :title="row.lab">
        <i v-for="(d, k) in row.dots" :key="k" :class="{ 'is-off': !d.on }"
           :style="{ left: d.x + '%', background: d.color }" :title="d.t"></i>
      </div>
    </template>
    <div class="ly-tl-axis">
      <template v-if="chrono"><span v-for="a in axis" :key="a.x" :style="{ left: a.x + '%' }">{{ a.t }}</span></template>
      <span v-else class="ly-tl-axis-note">порядок — как в ряду карточек внизу</span>
    </div>
    <template v-if="storyBlock !== null">
      <div v-for="row in dotRows" :key="'l' + row.key" class="ly-tl-leg">
        <em>{{ row.leg }}</em>
        <i v-for="t in row.list" :key="t"><b :style="{ background: hue(row.list, t) }"></b>{{ t }}</i>
      </div>
    </template>
  </div>
</template>

<script>
import { phraseColors, dayOf, noChurnTxt, headOf, dayLabel, monthLabel } from '@/utils/layers';

// Цвет шарика — от самого текста: список уникальных строк отсортирован, поэтому при любой
// пересортировке ряда цвет за текстом закреплён и не пляшет.
const TL_HUE = [212, 28, 268, 160, 340, 88, 190, 312, 48, 240, 128, 356, 66, 292, 174];
const dateOf = day => new Date(day * 864e5);          // московские сутки → UTC-дата

// Лента показывает ровно тот ряд, что в сцене: столбик — карточка, порядок — units.
// units — весь ряд без отбора окном: окно гасит столбики, а не съедает шкалу, иначе
// вернуть его расширением было бы нечем.
export default {
  name: 'LayerTimeline',
  props: {
    units: { type: Array, default: () => [] },        // порядок ряда
    range: { default: null },                         // окно ленты времени или null — весь период
    chrono: { type: Boolean, default: false },        // шаг по датам и без сортировки: даты и ось месяцев (П 6954)
    storyBlock: { type: Number, default: null },
    tlMode: { type: String, default: null },
    nBlocks: { type: Number, default: 0 },            // число блоков воронки; 0 — взять по ряду
    view: { type: Object, default: null },            // {a, b} — столбики карточек, видимых в сцене; null — не показывать
    here: { type: Number, default: null },            // столбик передней карточки сцены; null — курсора нет
    numOf: { type: Function, default: null },         // сессия → хронологический номер, как в метках сцены
  },
  emits: ['scroll-to', 'range', 'tl-mode'],
  data() {
    return { dotT: false, dotS: false, drag: null, hover: null };
  },
  computed: {
    n() { return this.units.length; },
    mode() { return this.tlMode || 'scroll'; },
    // без nBlocks секций столько, сколько блоков дошёл самый глубокий посетитель ряда
    nB() {
      return this.nBlocks > 0 ? this.nBlocks
        : Math.max(1, this.units.reduce((m, u) => Math.max(m, u.reached, u.churn), -1) + 1);
    },
    minD() { return this.units.reduce((m, u) => Math.min(m, u.day), Infinity); },
    maxD() { return this.units.reduce((m, u) => Math.max(m, u.day), -Infinity); },
    inRange() {
      const r = this.range;
      return this.units.map(u => !r || (u.day >= r.from && u.day <= r.to));
    },
    inWin() { return this.inRange.filter(Boolean).length; },
    colors() { return phraseColors(this.units.map(u => u.phrase)); },
    // точка конверсии — у дня, когда конверсия случилась, а не у дня начала сессии
    convDays() {
      const s = new Set();
      this.units.forEach(u => { if (u.convMask) s.add(u.convFirst ? dayOf(u.convFirst) : u.day); });
      return s;
    },
    bars() {
      const n = this.n;
      // На тысячах карточек доля ряда уже пары пикселей: зазор съел бы столбик целиком
      // (ширина ушла бы в минус и лента опустела), поэтому зазор снимаем, а ширину держим от 1 px.
      const gap = n > 200 ? 0 : 2;
      const width = `max(1px, min(calc(${(100 / n).toFixed(4)}% - ${gap}px), 13px))`;
      return this.units.map((u, i) => ({
        id: u.id, on: this.inRange[i], conv: this.convDays.has(u.day), hb: u.churn >= 0 ? u.churn + 1 : 0,
        color: this.colors[u.phrase],
        style: { left: ((i + 0.5) / n * 100).toFixed(4) + '%', width,
          '--bc': this.colors[u.phrase] },
      }));
    },
    selRect() {
      if (this.drag && this.drag.sel) return this.drag.sel;
      if (!this.range) return null;
      const a = this.inRange.indexOf(true), b = this.inRange.lastIndexOf(true);
      return a < 0 ? null : this.rect(a, b);
    },
    axis() {
      const out = [];
      let mk = null;
      this.units.forEach((u, i) => {
        const d = dateOf(u.day), k = d.getUTCFullYear() * 12 + d.getUTCMonth();
        if (k !== mk) { out.push({ x: (i + 0.5) / this.n * 100, t: monthLabel(u.day) }); mk = k; }
      });
      return out;
    },
    dotRows() {
      const rows = [];
      const mk = (key, lab, leg, get) => {
        const vals = this.units.map(u => get(headOf(u)));
        const list = [...new Set(vals)].sort((a, b) => a.localeCompare(b, 'ru'));
        rows.push({ key, lab, leg, list, dots: vals.map((t, i) => ({
          t, on: this.inRange[i], x: (i + 0.5) / this.n * 100, color: this.hue(list, t) })) });
      };
      // без заголовка — служебный текст, как на плашках и в решётке; пустой подзаголовок — «—», как в разборе
      if (this.dotT) mk('t', 'заголовок', 'заголовки', h => h.t);
      if (this.dotS) mk('s', 'подзаголовок', 'подзаголовки', h => (h.none ? h.t : h.s || '—'));
      return rows;
    },
    tip() {
      if (this.hover === null || this.drag || !this.units[this.hover]) return null;
      return { u: this.units[this.hover], x: (this.hover + 0.5) / this.n * 100 };
    },
  },
  beforeUnmount() { this.unbind(); },
  methods: {
    noChurnTxt,
    plural(n, f) {
      const a = Math.abs(n) % 100, b = a % 10;
      return a > 10 && a < 20 ? f[2] : b > 1 && b < 5 ? f[1] : b === 1 ? f[0] : f[2];
    },
    dayLabel,
    hue(list, t) { const i = list.indexOf(t); return i < 0 ? 'var(--grid)' : `hsl(${TL_HUE[i % TL_HUE.length]} 62% 48%)`; },
    rect(a, b) {
      return { left: (a / this.n * 100).toFixed(3) + '%', width: Math.max(0.6, (b + 1 - a) / this.n * 100).toFixed(3) + '%' };
    },
    setMode(m) {
      // Прокрутка по определению ничего не прячет: оставь окно висеть — кнопка переключится,
      // а ряд останется обрезанным. Поэтому на выходе из окна снимаем и сам отбор.
      if (m === 'scroll' && this.range) this.$emit('range', null);
      this.$emit('tl-mode', m);
    },
    frac(x) {
      const r = this.drag.r;
      return Math.max(0, Math.min(1, (x - r.left) / r.width));
    },
    slotAt(x) { return Math.max(0, Math.min(this.n - 1, Math.floor(this.frac(x) * this.n))); },
    onHover(ev) {
      if (this.drag) return;
      const r = this.$refs.track.getBoundingClientRect();
      this.hover = Math.max(0, Math.min(this.n - 1, Math.floor((ev.clientX - r.left) / r.width * this.n)));
    },
    onDown(ev) {
      if (ev.button !== 0) return;
      ev.preventDefault();
      this.drag = { r: this.$refs.track.getBoundingClientRect(), x0: ev.clientX, moved: false, sel: null, range: null };
      if (this.mode === 'scroll') this.$emit('scroll-to', this.frac(ev.clientX));
      window.addEventListener('mousemove', this.onMove);
      window.addEventListener('mouseup', this.onUp);
    },
    onMove(ev) {
      const d = this.drag;
      if (!d) return;
      if (Math.abs(ev.clientX - d.x0) > 4) d.moved = true;
      if (this.mode === 'scroll') { this.$emit('scroll-to', this.frac(ev.clientX)); return; }
      if (!d.moved) return;
      const ia = this.slotAt(Math.min(d.x0, ev.clientX)), ib = this.slotAt(Math.max(d.x0, ev.clientX));
      // при сортировке ряд не хронологический, поэтому окно — размах дат протянутых карточек
      const days = this.units.slice(ia, ib + 1).map(u => u.day);
      // reduce, а не спред: в окно могут попасть десятки тысяч карточек, спред упал бы с RangeError
      d.range = { from: days.reduce((m, v) => Math.min(m, v), Infinity), to: days.reduce((m, v) => Math.max(m, v), -Infinity) };
      d.sel = this.rect(ia, ib);
    },
    onUp(ev) {
      const d = this.drag;
      this.unbind();
      if (!d) return;
      const slot = this.slotAt(ev.clientX);          // frac читает drag.r — до обнуления
      this.drag = null;
      if (this.mode === 'scroll') {
        // клик без протяжки — сцена едет к середине столбика, а не к точке курсора
        if (!d.moved) this.$emit('scroll-to', (slot + 0.5) / this.n);
        return;
      }
      if (d.moved && d.range) { this.$emit('range', d.range); return; }
      // клик — окно в один день этой карточки; повторный клик по нему снимает окно
      const day = this.units[slot].day;
      const r = this.range;
      this.$emit('range', r && r.from === day && r.to === day ? null : { from: day, to: day });
    },
    unbind() {
      window.removeEventListener('mousemove', this.onMove);
      window.removeEventListener('mouseup', this.onUp);
    },
  },
};
</script>

<style>
.ins-root .ly-tline{border:1px solid var(--border); border-radius:12px; background:var(--page);
  padding:10px 14px 8px; margin:-4px 0 16px;}
.ins-root .ly-tl-head{display:flex; align-items:center; gap:12px; margin-bottom:8px; flex-wrap:wrap;}
.ins-root .ly-tl-info{font-size:12px; color:var(--text-secondary); font-variant-numeric:tabular-nums;}
.ins-root .ly-tl-acc{color:var(--accent);}
/* класс ленты сильнее общих .tgl/.btn из insights.css (тот подключается позже) */
.ins-root .ly-tline .ly-tl-mini{padding:2px 9px; font-size:11px; line-height:15px; border-radius:999px;}
/* «весь период» — chip ghosty, как кнопки LayerBar */
.ins-root .ly-tline .tgl.ly-ghost{padding:5px 10px; font-size:12.5px; color:var(--text-muted);}
.ins-root .ly-tl-lg{font-size:11px; color:var(--text-muted); display:inline-flex; align-items:center; gap:5px;}
.ins-root .ly-tl-lg b{width:8px; height:8px; border-radius:2px; display:inline-block;}
.ins-root .ly-tl-sw{display:inline-flex; align-items:center; gap:4px;}
.ins-root .ly-tl-how{margin-left:auto;}
.ins-root .ly-tl-track{position:relative; height:58px; cursor:crosshair; user-select:none; border-bottom:1px solid var(--grid);}
.ins-root .ly-tl-track.is-scroll{cursor:ew-resize;}
/* Столбик = одна карточка ряда: секции по числу блоков, сверху линия цвета фразы,
   как кант над карточкой; граница секций тем же цветом — столбик читается деталью своей фразы. */
.ins-root .ly-tl-bar{position:absolute; top:0; bottom:0; transform:translateX(-50%);
  display:flex; flex-direction:column; gap:1px; border-radius:2px; transition:opacity .15s;}
.ins-root .ly-tl-bar u{display:block; height:3px; border-radius:1.5px; text-decoration:none; flex:0 0 3px; margin-bottom:1px;}
.ins-root .ly-tl-bar s{display:block; flex:1 1 0; text-decoration:none; border-radius:1.5px;
  background:var(--surface-1); box-shadow:inset 0 0 0 1px var(--bc, var(--border));}
.ins-root .ly-tl-bar s.is-on{background:var(--accent);}
.ins-root .ly-tl-bar.is-conv::after{content:""; position:absolute; left:50%; bottom:-5px;
  width:4px; height:4px; margin-left:-2px; border-radius:999px; background:var(--ev-goal);}
.ins-root .ly-tl-bar.is-off{opacity:.32;}
.ins-root .ly-tl-bar.is-here{outline:1.5px solid var(--text-primary); outline-offset:1px;}
.ins-root .ly-tl-bar:hover{outline:1.5px solid var(--text-muted); outline-offset:1px;}
.ins-root .ly-tl-view{position:absolute; top:-2px; bottom:-2px; border-radius:6px; pointer-events:none;
  background:color-mix(in srgb, var(--text-muted) 12%, transparent);
  box-shadow:inset 0 0 0 1px color-mix(in srgb, var(--text-muted) 26%, transparent);}
.ins-root .ly-tl-sel{position:absolute; top:0; bottom:0; background:var(--brush-fill);
  border-left:1px solid var(--accent); border-right:1px solid var(--accent); pointer-events:none;}
.ins-root .ly-tl-cursor{position:absolute; top:-4px; bottom:-4px; width:2px; margin-left:-1px; background:var(--text-primary);
  border-radius:1px; z-index:4; box-shadow:0 0 0 3px var(--page); pointer-events:none;}
.ins-root .ly-tl-cursor::before{content:""; position:absolute; left:-4px; top:-5px; border:5px solid transparent;
  border-top-color:var(--text-primary);}
.ins-root .ly-tl-tip{position:absolute; bottom:calc(100% + 6px); transform:translateX(-50%); z-index:5;
  background:var(--tooltip-bg); color:var(--tooltip-ink); border-radius:8px; padding:5px 9px; font-size:11px;
  display:flex; flex-direction:column; white-space:nowrap; pointer-events:none;}
.ins-root .ly-tl-tipconv{color:var(--ev-goal);}
.ins-root .ly-tl-dots{position:relative; height:13px; margin-top:3px;}
.ins-root .ly-tl-dots i{position:absolute; top:2px; width:9px; height:9px; margin-left:-4.5px;
  border-radius:999px; box-shadow:0 0 0 1px var(--page);}
.ins-root .ly-tl-dots i.is-off{opacity:.28;}
.ins-root .ly-tl-leg{display:flex; flex-wrap:wrap; align-items:center; gap:3px 12px; margin-top:5px;}
.ins-root .ly-tl-leg em{font-style:normal; font-size:9.5px; color:var(--text-muted); margin-right:2px;}
.ins-root .ly-tl-leg i{display:flex; align-items:center; gap:5px; font-style:normal; font-size:9.5px; color:var(--text-secondary);}
.ins-root .ly-tl-leg i b{width:8px; height:8px; border-radius:999px; flex:0 0 auto;}
.ins-root .ly-tl-axis{position:relative; height:16px; font-size:10px; color:var(--text-muted);}
.ins-root .ly-tl-axis span{position:absolute; transform:translateX(-50%); white-space:nowrap; top:2px;}
.ins-root .ly-tl-axis span::before{content:""; position:absolute; left:50%; top:-4px; width:1px; height:4px; background:var(--grid);}
.ins-root .ly-tl-axis span.ly-tl-axis-note{left:0; transform:none;}
.ins-root .ly-tl-axis span.ly-tl-axis-note::before{content:none;}
</style>
